import { Service } from "@roc/feature-app-core";

const url = {
  GET_INSPECTION_PHOTOS_BY_INSPECTION_ID:
    '/api/v1/inspection/photos/getInspectionPhotosByInspectionId',
  GET_INSPECTION_DETAILS:
    '/api/v1/inspection/getInspectionDetails',
  GET_INSPECTION_PHOTO:
    '/api/v1/inspection/photos/downloadPhoto',
  GET_INSPECTION_PHOTO_THUMBNAIL:
    '/api/v1/inspection/photos/downloadThumbnail',
  GET_INSPECTION_THUMBNAILS:
    '/api/v1/inspection/photos/downloadInspectionThumbnails',
  PATCH_INSPECTION_PHOTO_ROTATION:
    '/api/v1/inspection/photos/{{inspectionPhotoId}}/preferredRotation',
  PATCH_INSPECTION_REPORT_PHOTO:
    '/api/v1/inspection/photos/{{inspectionPhotoId}}/report',
  POST_OVERRIDE_INSPECTION_PHOTO_VALIDATION:
    '/api/v1/inspection/photos/overrideValidation',
  UPDATE_INSPECTION_GEOLOCATION_DATA:
    '/api/v1/inspection/updateInspectionGeoLocationData',
  GET_PROPERTIES_BY_LOAN_ID: '/api/v1/inspection/loans/{{loanId}}/properties',
  GET_DRAWS_BY_PROPERTY_ID:
    '/api/v1/inspection/loans/{{loanId}}/properties/{{propertyId}}/draws',
  POST_MOVE_PHOTOS: '/api/v1/inspection/photos/movePhotos',
  POST_COPY_PHOTOS: '/api/v1/inspection/photos/copyPhotos',
  GET_INSPECTION_DETAILS_BY_EXTERNAL_IDS: '/api/v1/inspection/getInspectionDetailsByExternalIds',
  POST_UPLOAD_MANUAL_PHOTO: '/api/v1/inspection/photos/manual',
  POST_MOVE_MANUAL_PHOTOS_TO_SECTION: '/api/v1/inspection/photos/manual/moveToSection',
}

export class InspectionPhotosService extends Service {

  getInspectionDetails(inspectionId: number) {
    return this.get(url.GET_INSPECTION_DETAILS, { inspectionId });
  }

  getInspectionDetailsByExternalIds(externalId1: number, externalId2: number) {
    return this.get(url.GET_INSPECTION_DETAILS_BY_EXTERNAL_IDS, { externalId1, externalId2 });
  }

  getInspectionPhotosByInspectionId(inspectionId: number) {
    return this.get(url.GET_INSPECTION_PHOTOS_BY_INSPECTION_ID, { inspectionId });
  }

  getInspectionPhoto(inspectionPhotoUuid: string) {
    return this.get(url.GET_INSPECTION_PHOTO, { photoKey: inspectionPhotoUuid }, { responseType: 'blob', disableGlobalLoading: true });
  }

  getInspectionPhotoThumbnail(inspectionPhotoUuid: string) {
    return this.get(url.GET_INSPECTION_PHOTO_THUMBNAIL, { photoKey: inspectionPhotoUuid }, { responseType: 'blob', disableGlobalLoading: true });
  }

  async getInspectionThumbnails(inspectionId: any) {
    return this.get(
      url.GET_INSPECTION_THUMBNAILS,
      { inspectionId: inspectionId },
      { responseType: 'blob', disableGlobalLoading: true }
    ).then(r => new Response(r.data, { headers: r.headers }).formData());
  }

  patchInspectionPhotoRotation(inspectionPhotoId: number, rotationDegrees: number) {
    return this.patch(
      url.PATCH_INSPECTION_PHOTO_ROTATION.replace("{{inspectionPhotoId}}", `${inspectionPhotoId}`),
      { rotationDegrees },
      null,
      { disableGlobalLoading: true });
  }

  overrideInspectionPhotoValidation(data) {
    return this.post(url.POST_OVERRIDE_INSPECTION_PHOTO_VALIDATION, data);
  }

  updateInspectionGeoLocationData(data: { inspectionId: number, latitude: number, longitude: number }) {
    return this.post(url.UPDATE_INSPECTION_GEOLOCATION_DATA, data);
  }

  getPropertiesByLoanId(loanId: string) {
    return this.get(
      url.GET_PROPERTIES_BY_LOAN_ID.replace('{{loanId}}', loanId)
    );
  }

  getDrawsByPropertyId(loanId: string, propertyId: string) {
    return this.get(
      url.GET_DRAWS_BY_PROPERTY_ID.replace('{{loanId}}', loanId).replace(
        '{{propertyId}}',
        propertyId
      )
    );
  }

  movePhotos(data: {
    selectedPhotos: number[];
    sourceInspectionId: number;
    destinationInspectionId: number;
  }) {
    return this.post(url.POST_MOVE_PHOTOS, data);
  }

  copyPhotos(data: {
    selectedPhotos: number[];
    sourceInspectionId: number;
    destinationInspectionId: number;
  }) {
    return this.post(url.POST_COPY_PHOTOS, data);
  }

  patchInspectionPhotoReported(inspectionPhotoId: number, reported: boolean) {
    return this.patch(
      url.PATCH_INSPECTION_REPORT_PHOTO.replace("{{inspectionPhotoId}}", `${inspectionPhotoId}`),
      { reported }
    );
  }

  uploadManualPhoto(inspectionId: number, inspectionTaskId: number, file: File, caption?: string) {
    const formData = new FormData();
    formData.append(
      'metadata',
      JSON.stringify({
        inspectionId,
        inspectionTaskId,
        mimeType: file.type,
        caption: caption || null,
      })
    );
    formData.append('photo', file);
    return this.post(url.POST_UPLOAD_MANUAL_PHOTO, formData);
  }

  moveManualPhotosToSection(data: {
    inspectionId: number;
    photoIds: number[];
    destinationInspectionTaskId: number;
  }) {
    return this.post(url.POST_MOVE_MANUAL_PHOTOS_TO_SECTION, data);
  }
}
